import React, { useState } from "react";
import PropTypes from "prop-types";
import Col from "../../grid/Col";
import Row from "../../grid/Row";
import { Image } from "../Image/Image";
import { LightboxProvider } from "../Lightbox";
import { ImageUploadProgress } from "./ImageUploadProgress";
import { FormattedMessage } from "react-intl";
import { Text } from "../../typography/Text";

const msgKey = "imageGallery";

export const ImageGallery = ({ images, onUpload, onRefresh, onRemove }) => {
    const [uploadQueue, setUploadQueue] = useState(null);
    const [removingFileId, setRemovingFileId] = useState(null);

    const handleFileSelect = (files) => {
        if (!files?.length) return;

        const queue = {};
        files.forEach((file, index) => {
            queue[index] = { file, percent: 0 };
        });
        setUploadQueue(queue);

        const promises = files.map((file, index) =>
            onUpload(file, (percent) => {
                setUploadQueue((q) => ({ ...q, [index]: { file, percent } }));
            })
        );

        Promise.all(promises).finally(() => {
            setUploadQueue(null);
            onRefresh?.();
        });
    };

    const handleRemove = async (fileId) => {
        setRemovingFileId(fileId);
        await onRemove(fileId);
        setRemovingFileId(null);
        await onRefresh?.();
    };

    return (
        <LightboxProvider>
            <Row gutter={["small", "small"]}>
                {images?.map((file) => (
                    <Col key={file.id} xs={8} md={6}>
                        <Image
                            src={file.url}
                            size="fill"
                            lightbox
                            alt={file.name}
                            onRemove={onRemove && !removingFileId ? () => handleRemove(file.id) : null}
                            busy={removingFileId === file.id}
                        />
                    </Col>
                ))}

                {onUpload ? (
                    <Col xs={8} md={6}>
                        <Image size="fill" icon="plus-small" onFileSelect={handleFileSelect} multiple />
                    </Col>
                ) : images?.length === 0 ? (
                    <Col span={24}>
                        <Text color="base40">
                            <FormattedMessage id={`${msgKey}.noImages`} />
                        </Text>
                    </Col>
                ) : null}
            </Row>

            <ImageUploadProgress uploadQueue={uploadQueue} />
        </LightboxProvider>
    );
};

ImageGallery.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            url: PropTypes.string,
            name: PropTypes.string
        })
    ),
    onUpload: PropTypes.func,
    onRefresh: PropTypes.func
};
